import React, { useEffect, useRef } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import { useFonts } from '@/providers/font'
import type { OnShapeCommitted } from '../types/on-shape-committed'
import { fallBackTextShapeFonts } from '../handlers/fall-back-text-shape-fonts'

export function BlockedFontsChecker({
  shapeManagerRef,
  onShapeCommitted,
}: {
  shapeManagerRef: React.RefObject<ShapeManagerRef | null>
  onShapeCommitted?: OnShapeCommitted
}) {
  const { failedFontFamilyNames, getFamilyIdByCssName } = useFonts()
  const appliedNamesRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (failedFontFamilyNames.size === 0) return

    const newlyFailed = [...failedFontFamilyNames].filter((n) => !appliedNamesRef.current.has(n))

    if (newlyFailed.length === 0) return

    for (const n of newlyFailed) appliedNamesRef.current.add(n)

    const failedFamilyIds = new Set(
      newlyFailed.map((n) => getFamilyIdByCssName(n)).filter((id): id is number => id != null)
    )

    if (failedFamilyIds.size === 0) return

    // Text-unification Phase 2: a text frame is a ShapeManager shape carrying `paragraphs`, not a
    // TextManager box (which never renders any content now — see CLAUDE.md Known Gap #20).
    const textShapes = (shapeManagerRef.current?.getShapes() ?? []).filter(isShapeTextFrame)

    fallBackTextShapeFonts(textShapes, failedFamilyIds, shapeManagerRef, onShapeCommitted)
  }, [failedFontFamilyNames, shapeManagerRef, onShapeCommitted, getFamilyIdByCssName])

  return null
}
